import { Link } from "react-router-dom";
import type { PublicDealsRetailer } from "../../config/publicDeals";
import { buildInstallUrl } from "../../services/AttributionService";
import { BASIC_INTRO_LABEL, BASIC_PRICE_LABEL } from "../../utils/basicPlanOffer";
import { HF, retailerLabel } from "./homeFeedUi";
import { AppleLogo, PlayLogo, StoreBadge } from "./SiteFooter";

/** Brand fills that stay the same in both themes. */
export const BRAND = "#3A0A63";
export const HIGHLIGHT = "#FFE14D";
export const HIGHLIGHT_INK = "#2B1A00";

// App Store average for com.dealhawkpro.pinto (iTunes lookup, 89 ratings on 2026-10-17).
const APP_RATING = "4.8";
const APP_RATING_COUNT = 89;

/** The yellow line above the header: the intro offer, one tap away. */
export function OfferStrip({ to, onClick, upgrade }: { to: string; onClick: () => void; upgrade?: boolean }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex min-h-[34px] items-center justify-center gap-1.5 px-4 py-1.5 text-center text-[13px] no-underline sm:min-h-9 sm:text-[14px]"
      style={{ background: HIGHLIGHT, color: HIGHLIGHT_INK }}
      data-testid="landing-offer-strip"
    >
      <span>
        <strong className="font-bold">50% off your first month</strong>
        <span className="hidden sm:inline"> of DealHawk Basic: {BASIC_INTRO_LABEL}, then {BASIC_PRICE_LABEL}/mo.</span>
        <span className="sm:hidden"> · {BASIC_INTRO_LABEL}</span>
      </span>
      <span className="font-bold underline underline-offset-[3px]">{upgrade ? "Upgrade" : "Claim it"}</span>
    </Link>
  );
}

const TABS = [
  { href: "#top", label: "Home" },
  { href: "#clearance-near-you", label: "Clearance near you" },
  { href: "#online-deals", label: "Online deals" },
  { href: "#member-hauls", label: "Member hauls" },
  { href: "#pricing", label: "Pricing" },
];

/** White bar under the header: the page's sections, then the online stores as chips. */
export function LandingNav({ stores }: { stores: PublicDealsRetailer[] }) {
  return (
    <div className="border-b" style={{ borderColor: HF.border, background: HF.page }} data-testid="landing-nav">
      <div className="mx-auto flex max-w-[1384px] flex-col lg:flex-row lg:items-center lg:gap-1 lg:px-8">
        <nav aria-label="Sections" className="scrollbar-hide hidden flex-shrink-0 overflow-x-auto whitespace-nowrap lg:-ml-3.5 lg:flex" style={{ borderColor: HF.border }}>
          {TABS.map((tab, index) => (
            <a
              key={tab.href}
              href={tab.href}
              aria-current={index === 0 ? "page" : undefined}
              className="flex h-12 flex-shrink-0 items-center px-3 text-[15px] no-underline lg:h-14 lg:px-3.5"
              style={index === 0 ? { color: HF.brandText, fontWeight: 700, boxShadow: `inset 0 -3px 0 ${HF.brandText}` } : { color: HF.text2, fontWeight: 500 }}
            >
              {tab.label}
            </a>
          ))}
        </nav>
        <span className="mx-2.5 hidden h-6 w-px lg:block" style={{ background: HF.chipBorder }} aria-hidden="true" />
        <nav aria-label="Online stores" className="scrollbar-hide flex min-w-0 gap-2 overflow-x-auto whitespace-nowrap px-4 py-3 lg:flex-1 lg:px-0 lg:py-2">
          {stores.map((retailer) => (
            <Link
              key={retailer.slug}
              to={`/${retailer.slug}`}
              className="flex h-9 flex-shrink-0 items-center rounded-full px-3.5 text-[14px] font-medium no-underline transition-colors hover:brightness-95"
              style={{ background: HF.surface2, color: HF.ink }}
            >
              {retailerLabel(retailer)}
            </Link>
          ))}
          <Link to="/stores" className="flex h-9 flex-shrink-0 items-center gap-1 px-2.5 text-[14px] font-semibold no-underline" style={{ color: HF.brandText }}>
            All 60+
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
          </Link>
        </nav>
      </div>
    </div>
  );
}

/** Real DealHawk app screens (dark mode, captured for the store listings), one per member feature. */
const APP_SCREENS = [
  {
    id: "feed",
    src: "/landing/app/hd-feed-walmart.webp",
    title: "Every markdown at your stores",
    text: "Shelf price, store, and units left, updated through the day.",
    alt: "DealHawk app showing Walmart clearance with shelf prices, stores and units available",
  },
  {
    id: "detail",
    src: "/landing/app/member-detail.webp",
    title: "Know the aisle before you go",
    text: "Aisle H22, 4 units, and the price history behind it.",
    alt: "DealHawk app item page with price, aisle, units, other stores and a price chart",
  },
  {
    id: "find",
    src: "/landing/app/hd-search.webp",
    title: "Cheapest store nearby",
    text: "Same Hoover: $187 at two stores, $319.99 at another.",
    alt: "DealHawk app Find it in store results listing nearby stores with price and units on hand",
  },
  {
    id: "alerts",
    src: "/landing/app/hd-alerts.webp",
    title: "A ping at your price",
    text: "Set a target. We tell you when it hits, and where.",
    alt: "DealHawk app Price Alerts list with items under their target price",
  },
];

/** The app, shown with its own screens: purple band, rating, store badges, four phones. */
export function AppShowcase() {
  return (
    <section
      aria-labelledby="app-showcase-title"
      className="-mx-4 overflow-hidden px-5 pt-9 text-white sm:mx-0 sm:rounded-[32px] sm:px-10 sm:pt-12 lg:px-[52px] lg:pt-[52px]"
      style={{ background: BRAND }}
      data-testid="landing-app-showcase"
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex max-w-[580px] flex-col gap-3">
          <span className="text-[14px] font-semibold sm:text-[15px]" style={{ color: HIGHLIGHT }}>The DealHawk app</span>
          <h2 id="app-showcase-title" className="m-0 text-[31px] font-bold leading-[1.06] tracking-[-0.8px] sm:text-[44px] sm:tracking-[-1.2px]">
            The exact price, the aisle, and how many are left
          </h2>
          <span className="flex items-center gap-2 text-[14px] sm:text-[15px]" style={{ color: "rgba(255,255,255,0.8)" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill={HIGHLIGHT} aria-hidden="true"><path d="m12 2 3 6.6 7 .7-5.3 4.7 1.6 7L12 17.3 5.7 21l1.6-7L2 9.3l7-.7z" /></svg>
            <strong className="text-white">{APP_RATING}</strong> on the App Store · {APP_RATING_COUNT} ratings
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2.5 sm:flex sm:gap-3">
          <StoreBadge href={buildInstallUrl("ios")} caption="Download on the" store="App Store" icon={<AppleLogo />} />
          <StoreBadge href={buildInstallUrl("android")} caption="Get it on" store="Google Play" icon={<PlayLogo />} />
        </div>
      </div>
      <div className="scrollbar-hide -mx-5 mt-7 flex snap-x scroll-px-5 gap-4 overflow-x-auto px-5 sm:mx-0 sm:mt-9 sm:grid sm:grid-cols-2 sm:gap-7 sm:overflow-visible sm:px-0 lg:grid-cols-4">
        {APP_SCREENS.map((screen) => (
          <figure key={screen.id} className="m-0 flex w-[236px] flex-shrink-0 snap-start flex-col gap-3 sm:w-auto sm:gap-4">
            <figcaption className="flex min-h-[58px] flex-col gap-1">
              <b className="text-[16px] sm:text-[18px]">{screen.title}</b>
              <span className="text-[13px] leading-[1.4] sm:text-[14px]" style={{ color: "rgba(255,255,255,0.72)" }}>{screen.text}</span>
            </figcaption>
            <span className="block h-[380px] overflow-hidden rounded-t-[30px] px-2 pt-2 sm:h-[400px] sm:rounded-t-[34px] sm:px-[9px] sm:pt-[9px]" style={{ background: "#120620", boxShadow: "0 0 0 1px rgba(255,255,255,0.14)" }}>
              <img src={screen.src} alt={screen.alt} loading="lazy" decoding="async" className="block w-full rounded-t-[23px] sm:rounded-t-[26px]" />
            </span>
          </figure>
        ))}
      </div>
    </section>
  );
}

export function LockGlyph({ size = 20, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

/** "$352.99" → big dollars with raised cents. */
export function SalePrice({ value }: { value: number }) {
  const [dollars, cents] = value.toFixed(2).split(".");
  return (
    <span className="text-[19px] font-bold leading-none tracking-[-0.3px] tabular-nums sm:text-[21px]" style={{ color: HF.ink }}>
      ${Number(dollars).toLocaleString("en-US")}
      <span className="align-[5px] text-[12px] sm:align-[6px] sm:text-[13px]">{cents}</span>
    </span>
  );
}
